import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { LayoutGrid, Save, ArrowLeft, AlertTriangle } from "lucide-react";
import { PLANTILLAS_MESAS as T } from "@/constants/testIds";
import { getAdminSession, puedeAccederModulo } from "@/data/adminAuthStore";
import { computarCapacidad } from "@/data/adminEventosStore";
import PasoPlanoMesas from "@/screens/admin/eventos/PasoPlanoMesas";
import {
  getCatalogoSalones,
  obtenerPlantilla,
  crearPlantilla,
  editarPlantilla,
  construirPayloadPlantilla,
} from "@/data/plantillasMesasStore";

export default function PlantillaMesasEditor() {
  const navigate = useNavigate();
  const { id } = useParams();
  const esNueva = !id || id === "nueva";
  const rol = getAdminSession()?.rol || null;
  const autorizado = puedeAccederModulo(rol, "plantillasMesas");

  const [salones, setSalones] = useState([]);
  const [nombre, setNombre] = useState("");
  const [salon, setSalon] = useState("");
  const [mesas, setMesas] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(!esNueva);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [confirmSalir, setConfirmSalir] = useState(false);

  useEffect(() => {
    if (!autorizado) return;
    getCatalogoSalones().then(setSalones).catch(() => setSalones([]));
  }, [autorizado]);

  useEffect(() => {
    if (esNueva || !autorizado) return;
    obtenerPlantilla(id)
      .then((p) => {
        setNombre(p.nombre || "");
        setSalon(p.salon || "");
        setMesas(Array.isArray(p.mesas) ? p.mesas : []);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [id, esNueva, autorizado]);

  // Aviso nativo si hay cambios sin guardar al recargar/cerrar.
  useEffect(() => {
    const h = (e) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const valores = useMemo(
    () => ({ salones: salon ? [salon] : [], salon, mesas, modalidad: "MESAS" }),
    [salon, mesas],
  );

  const onChange = useCallback((campo, valor) => {
    setDirty(true);
    if (campo === "mesas") setMesas(Array.isArray(valor) ? valor : []);
    else if (campo === "salones") setSalon((valor && valor[0]) || "");
  }, []);

  const capacidad = computarCapacidad(mesas);

  const salir = () => navigate("/admin/plantillas-mesas");
  const intentarSalir = () => (dirty ? setConfirmSalir(true) : salir());

  const guardar = async () => {
    setError("");
    if (!nombre.trim()) return setError("El nombre de la plantilla es obligatorio.");
    if (!salon) return setError("Selecciona un salón oficial.");
    if (!mesas.length) return setError("Agrega al menos una mesa.");
    setGuardando(true);
    try {
      const payload = construirPayloadPlantilla({ nombre, salon, mesas });
      if (esNueva) await crearPlantilla(payload);
      else await editarPlantilla(id, payload);
      setDirty(false);
      toast.success(esNueva ? "Plantilla creada." : "Cambios guardados.");
      navigate("/admin/plantillas-mesas");
    } catch (e) {
      setError(e.message);
      if (e.status === 409) toast.error(e.message);
    } finally {
      setGuardando(false);
    }
  };

  if (!autorizado) {
    return (
      <div data-testid={T.denied} className="min-h-[60vh] grid place-items-center text-center px-6">
        <p className="text-sm text-muted-foreground">No tienes permiso para administrar plantillas de mesas.</p>
      </div>
    );
  }
  if (loading) {
    return <p className="max-w-6xl mx-auto px-4 md:px-8 py-8 text-sm text-muted-foreground">Cargando plantilla…</p>;
  }

  return (
    <div data-testid={T.editorScreen} className="max-w-6xl mx-auto px-4 md:px-8 py-6 pb-28 space-y-6">
      <header className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <LayoutGrid className="w-6 h-6 text-accent" strokeWidth={1.7} />
          <h1 className="text-2xl font-semibold">
            {esNueva ? "Nueva plantilla de mesas" : "Editar plantilla de mesas"}
          </h1>
        </div>
      </header>

      {error && (
        <div data-testid={T.editorError} className="rounded-xl border border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-300 px-4 py-3 text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium mb-1.5 block" htmlFor="plt-nombre">Nombre de la plantilla</label>
          <input
            id="plt-nombre"
            data-testid={T.editorNombre}
            value={nombre}
            onChange={(e) => { setNombre(e.target.value); setDirty(true); }}
            placeholder="Ej. Gala 40 mesas"
            className="w-full rounded-xl border hairline bg-background px-3 min-h-[44px] text-sm outline-none focus:ring-2 focus:ring-accent/40"
          />
        </div>
        <div>
          <label className="text-sm font-medium mb-1.5 block" htmlFor="plt-salon">Salón oficial</label>
          <select
            id="plt-salon"
            data-testid={T.editorSalon}
            value={salon}
            onChange={(e) => { setSalon(e.target.value); setDirty(true); }}
            className="w-full rounded-xl border hairline bg-background px-3 min-h-[44px] text-sm outline-none focus:ring-2 focus:ring-accent/40"
          >
            <option value="">Selecciona un salón…</option>
            {salones.map((s) => (
              <option key={s.nombre} value={s.nombre}>{s.nombre}</option>
            ))}
          </select>
        </div>
      </div>

      <div data-testid={T.editorResumen} className="flex flex-wrap gap-4 text-sm text-muted-foreground">
        <span>Total de mesas: <b className="text-foreground">{mesas.length}</b></span>
        <span>Capacidad total: <b className="text-foreground">{capacidad}</b></span>
      </div>

      {salon ? (
        <PasoPlanoMesas valores={valores} onChange={onChange} errores={{}} soloDisponible />
      ) : (
        <div className="rounded-xl border hairline bg-muted/40 px-4 py-8 text-center text-sm text-muted-foreground">
          Selecciona un salón oficial para configurar el plano de mesas.
        </div>
      )}

      {/* Barra de acciones */}
      <div className="fixed bottom-0 left-0 right-0 md:left-72 z-30 border-t hairline glass">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-3 flex items-center justify-end gap-3">
          <button
            type="button"
            data-testid={T.editorCancelar}
            onClick={intentarSalir}
            className="inline-flex items-center justify-center gap-1.5 rounded-xl px-4 min-h-[44px] text-sm font-medium border hairline bg-card hover:bg-muted"
          >
            <ArrowLeft className="w-4 h-4" /> Cancelar
          </button>
          <button
            type="button"
            data-testid={T.editorGuardar}
            onClick={guardar}
            disabled={guardando}
            className="inline-flex items-center justify-center gap-1.5 rounded-xl px-5 min-h-[44px] text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            <Save className="w-4 h-4" /> {guardando ? "Guardando…" : "Guardar plantilla"}
          </button>
        </div>
      </div>

      {confirmSalir && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4">
          <div data-testid={T.editorConfirmSalir} role="alertdialog" aria-modal="true"
               className="w-full max-w-md rounded-2xl border hairline bg-card p-6 shadow-xl space-y-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              <h2 className="text-lg font-semibold">Cambios sin guardar</h2>
            </div>
            <p className="text-sm text-muted-foreground">Si sales ahora perderás los cambios no guardados.</p>
            <div className="flex justify-end gap-3">
              <button type="button" data-testid={T.editorConfirmSalirCancel} onClick={() => setConfirmSalir(false)}
                      className="inline-flex items-center justify-center rounded-full px-4 min-h-[44px] text-sm font-medium border hairline bg-card hover:bg-muted">
                Seguir editando
              </button>
              <button type="button" data-testid={T.editorConfirmSalirOk} onClick={salir}
                      className="inline-flex items-center justify-center rounded-full px-4 min-h-[44px] text-sm font-medium bg-red-600 text-white hover:bg-red-700">
                Salir sin guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
